import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicDialog from '../../../u2000Core/components/dialog/U2000BasicDialog';
import { NS } from '../../U3147';
import {
  u3147FormatProjectLabel,
  u3147FormatSubprojectLabel,
  u3147FormatTaskLabel,
  u3147IsSessionExported,
  u3147IsSessionRunning,
  type U3147DayResult,
} from '../../U3147Service';
import { u3147FormatMinutesAsHoursMinutes, u3147FormatTimeFromIso } from '../../tools/u3147FormatDuration';
import { useU3147EnrichSessionsTasks } from '../../U3147UseEnrichSessionsTasks';

export interface U3147DayDetailReadonlyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  dayResult: U3147DayResult | null;
  showSubproject: boolean;
}

const U3147DayDetailReadonlyDialog: React.FC<U3147DayDetailReadonlyDialogProps> = ({
  isOpen,
  onClose,
  date,
  dayResult,
  showSubproject,
}) => {
  const { t, i18n } = useTranslation();
  const sessions = dayResult?.sessions ?? [];
  const displaySessions = useU3147EnrichSessionsTasks(sessions);

  const footerTotalColSpan = showSubproject ? 4 : 3;

  const totalMinutes = useMemo(() => {
    if (typeof dayResult?.totalMinutes === 'number' && Number.isFinite(dayResult.totalMinutes)) {
      return dayResult.totalMinutes;
    }
    let sum = 0;
    for (const s of displaySessions) {
      if (typeof s.durationMinutes === 'number' && Number.isFinite(s.durationMinutes)) {
        sum += s.durationMinutes;
      } else if (u3147IsSessionRunning(s) && s.startedAt) {
        sum += Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 60000);
      }
    }
    return sum;
  }, [dayResult?.totalMinutes, displaySessions]);

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      onBackdropClick={() => onClose()}
      width="x-large"
      title={t('lblDayDetailReadonly', {
        ns: NS,
        date,
        defaultValue: 'Détail du suivi de temps — {{date}}',
      })}
    >
      <div className="px-6 py-4">
        {displaySessions.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('msgEmptyDayReadonly', {
              ns: NS,
              defaultValue: 'Aucune plage pour ce jour',
            })}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colStart', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colEnd', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colDuration', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colProject', { ns: NS })}</th>
                  {showSubproject && (
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colSubproject', { ns: NS })}</th>
                  )}
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colTask', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colNote', { ns: NS })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displaySessions.map((session) => {
                  const running = u3147IsSessionRunning(session);
                  const exported = u3147IsSessionExported(session);
                  return (
                    <tr key={session.id} className={running ? 'bg-emerald-50/60' : undefined}>
                      <td className="px-3 py-2">{u3147FormatTimeFromIso(session.startedAt)}</td>
                      <td className="px-3 py-2">
                        {running ? t('lblRunning', { ns: NS, defaultValue: 'en cours' }) : u3147FormatTimeFromIso(session.endedAt)}
                      </td>
                      <td className="px-3 py-2">{u3147FormatMinutesAsHoursMinutes(session.durationMinutes ?? undefined)}</td>
                      <td className="px-3 py-2">{u3147FormatProjectLabel(session.project as never)}</td>
                      {showSubproject && (
                        <td className="px-3 py-2">{u3147FormatSubprojectLabel(session.subproject as never)}</td>
                      )}
                      <td className="px-3 py-2">{u3147FormatTaskLabel(session.task as never, i18n.language)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {exported && (
                            <span className="rounded-sm bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {t('lblExported', { ns: NS, defaultValue: 'Exportée' })}
                            </span>
                          )}
                          <span>{session.note ?? ''}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-medium">
                  <td colSpan={2} className="px-3 py-2 text-right">
                    {t('lblTotalDay', { ns: NS })}
                  </td>
                  <td className="px-3 py-2" colSpan={footerTotalColSpan + 1}>
                    {u3147FormatMinutesAsHoursMinutes(totalMinutes)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </U2000BasicDialog>
  );
};

export default U3147DayDetailReadonlyDialog;
